import {
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import axios from 'axios';
import clsx from 'clsx';
import { DefaultButton, colors, dateFormat, helpers } from 'design-system';
import React, { useEffect, useMemo, useRef } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { matchCurrentDate } from 'data-layer-cr/dist/functions/helpers';
import { Quotation } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Redux related
import {
  resetFiles,
  updateInspectionReportValue,
  updateVehicleRegisterValue,
} from '../../../../../../store/quotation/actions';
import { RootState } from '../../../../../../store/rootReducer';
import { setQuotationHistoryStepper } from '../../../../../../store/steppers/actions';

// Constants
import { PENDING_INSPECTION } from '../../../../../../constants/quotationStatus';
import {
  DOWNLOAD_INSPECTION_REPORT,
  DOWNLOAD_VEHICLE_REGISTRY,
} from '../../../../../../store/quotation/requestIdentifiers';

// Models
import QuotationChange from '../../../../../../models/Quotation/QuotationChange';

// Methods
import { statusChangeDateForId } from '../../../../../../functions/quotationStatusMethods';

// Components
import { CopyInspectionLinkButton } from '../../../components';
import BaseHistoryStepCard from '../BaseHistoryStepCard';

const { prettyDateFromServerString } = dateFormat;

const useStyles = makeStyles({
  smallText: {
    color: colors.abbey,
  },
  marginRow: {
    margin: '8px 0',
    width: '100%',
  },
});

/**
 * Creates an anchor tag and sets the provided base64 string as its download attribute,
 * so the user can save it in the disk
 *
 * @param fileBase64 - The base64 to download
 * @param fileName
 */
function downloadFile(fileBase64: string, fileName: string): void {
  let extension = '.pdf';
  const link = document.createElement('a');
  const mimeType = helpers.extractMime(fileBase64);

  if (mimeType === 'image/jpeg') {
    extension = '.jpeg';
  }

  if (mimeType === 'image/png') {
    extension = '.png';
  }

  link.href = fileBase64;
  link.setAttribute('target', '_blank');
  link.setAttribute('download', fileName + extension);
  document.body.appendChild(link);
  link.click();
}

/**
 * Triggers a redirect to the inspection workflow
 *
 * @param quotation - The current quotation
 */
function goToInspection(quotation: Quotation): void {
  window.open(
    `${process.env.REACT_APP_INSPECTION_URL}${quotation.urlSalt}`,
    '_blank',
  );
}

/**
 * Returns the required content for the card's subtitle
 *
 * @param isActiveStep - True if this is the current active step
 * @param changes - The list of changes related to the quotation
 */
function getSubtitle(
  isActiveStep: boolean,
  changes: QuotationChange[],
): string | undefined {
  const date = statusChangeDateForId(PENDING_INSPECTION, changes);
  if (isActiveStep) {
    return 'Puedes copiar el link y enviárselo a tu cliente por WhatsApp o por email, para que realice la inspección.';
  }
  if (!date) {
    return undefined;
  }
  return prettyDateFromServerString(date, false);
}

/**
 * Returns the required content for the card's title
 *
 * @param isActiveStep - True if this is the current active step
 */
function getTitle(isActiveStep: boolean): React.ReactNode {
  return isActiveStep ? (
    <b>Por inspección</b>
  ) : (
    <>
      Inspección <b>realizada</b>
    </>
  );
}

/**
 * Returns the required content for the download button
 *
 * @param isLoading - True if the network transaction is active
 */
function getButtonContent(isLoading: boolean): React.ReactNode {
  return !isLoading ? (
    <>Descargar reporte</>
  ) : (
    <>
      <CircularProgress size={12} data-testid="button-loading" />
      &nbsp;&nbsp;
      <Typography variant="caption">Descargando archivos...</Typography>
    </>
  );
}

/**
 * The card that exposes actions for a quotation whose status is PENDING_INSPECTION.
 */
export const InspectionStepCard = ({
  quotation,
  changes,
  inspectionReportBase64,
  vehicleRegisterBase64,
  updateInspectionReportValueAction,
  updateVehicleRegisterValueAction,
  setHistoryStep,
  resetFilesAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const inspectionReportStatus = useNetworkObserver(DOWNLOAD_INSPECTION_REPORT);
  const vehicleRegistryStatus = useNetworkObserver(DOWNLOAD_VEHICLE_REGISTRY);

  const cancelTokenSource = useRef(axios.CancelToken.source()).current;

  useEffect(() => {
    return (): void => {
      resetFilesAction();
      cancelTokenSource.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = useMemo(() => {
    return (
      inspectionReportStatus === 'requesting' ||
      vehicleRegistryStatus === 'requesting'
    );
  }, [inspectionReportStatus, vehicleRegistryStatus]);

  const isReportDefined = useMemo(() => {
    return ![inspectionReportBase64, vehicleRegisterBase64].includes('');
  }, [inspectionReportBase64, vehicleRegisterBase64]);

  useEffect(() => {
    if (isReportDefined) {
      downloadFile(inspectionReportBase64, 'inspectionReport');
      downloadFile(vehicleRegisterBase64, 'vehicleRegister');
    }
  }, [isReportDefined, inspectionReportBase64, vehicleRegisterBase64]);

  function downloadInspectionPdf(): void {
    if (isReportDefined) {
      downloadFile(inspectionReportBase64, 'inspectionReport');
      downloadFile(vehicleRegisterBase64, 'vehicleRegister');
    } else {
      updateVehicleRegisterValueAction(
        quotation.urlSalt,
        'vehicle-registry-image',
        cancelTokenSource,
      );
      updateInspectionReportValueAction(
        quotation.urlSalt,
        'inspectionReport',
        cancelTokenSource,
      );
    }
  }

  const isExpired = useMemo(
    () => matchCurrentDate(quotation.expireAt),
    [quotation],
  );
  const isActiveStep = useMemo(
    () => quotation.status.id === PENDING_INSPECTION,
    [quotation],
  );
  const isInspectionCompleted = useMemo(
    () => quotation.status.id !== PENDING_INSPECTION,
    [quotation],
  );
  const subTitle = useMemo(
    () => getSubtitle(isActiveStep, changes),
    [isActiveStep, changes],
  );
  const title = useMemo(() => getTitle(isActiveStep), [isActiveStep]);
  const buttonContent = useMemo(() => getButtonContent(isLoading), [isLoading]);

  const usedStatus = useMemo(() => {
    if (isExpired) {
      return 'expired';
    }
    if (isActiveStep) {
      return 'active';
    }
    return 'completed';
  }, [isExpired, isActiveStep]);

  return (
    <BaseHistoryStepCard title={title} subTitle={subTitle} status={usedStatus}>
      {isActiveStep && (
        <Grid data-testid="content" container spacing={1}>
          <Grid item xs={12}>
            <CopyInspectionLinkButton
              id="sales-web-button-inspectionlink"
              data-testid="inspection-link-button"
              disabled={isExpired}
              quotation={quotation}
              inspectionLink
            />
          </Grid>
          <Grid container justifyContent="center" item xs={12}>
            <Typography variant="caption" className={classes.smallText}>
              O si prefieres puedes
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <DefaultButton
              id="sales-web-button-gotoinspection"
              data-testid="go-to-inspection-button"
              fullWidth
              disabled={isExpired}
              onClick={(): void => goToInspection(quotation)}
            >
              Realizar la inspección
            </DefaultButton>
          </Grid>
          <Grid container item xs={12} alignItems="center" direction="column">
            <Typography
              variant="caption"
              align="center"
              className={clsx(classes.smallText, classes.marginRow)}
            >
              Ahora también puedes transferir una inspección a esta cotización
            </Typography>
            <div className={classes.marginRow}>
              <DefaultButton
                id="sales-web-button-inspectionsearchstart"
                variant="outlined"
                fullWidth
                onClick={(): void => setHistoryStep(1)}
                data-testid="inspection-search-btn"
                disabled={isExpired}
              >
                BUSCAR INSPECCIÓN
              </DefaultButton>
            </div>
          </Grid>
        </Grid>
      )}

      {isInspectionCompleted && (
        <Grid data-testid="content" container spacing={1}>
          <Grid item xs={12}>
            <DefaultButton
              id="sales-web-button-downloadreport"
              data-testid="inspectionReportButton"
              variant="outlined"
              fullWidth
              onClick={downloadInspectionPdf}
              disabled={isLoading || isExpired}
            >
              {buttonContent}
            </DefaultButton>
          </Grid>
        </Grid>
      )}
    </BaseHistoryStepCard>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  inspectionReportBase64: string;
  vehicleRegisterBase64: string;
} => ({
  inspectionReportBase64: state.quotation.inspectionReportBase64,
  vehicleRegisterBase64: state.quotation.vehicleRegisterBase64,
});

const reduxActions = {
  updateInspectionReportValueAction: updateInspectionReportValue,
  updateVehicleRegisterValueAction: updateVehicleRegisterValue,
  setHistoryStep: setQuotationHistoryStepper,
  resetFilesAction: resetFiles,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotation: Quotation;
  changes: QuotationChange[];
}

export default connector(InspectionStepCard);
